import { keys } from '@/gql/global/keys';
import { refetchQueriesForArchive } from '@/pages/CategoryManager';
import { useCronofySyncEventMany } from '@/services/cronofy/hooks';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { formatStringToDate, getGMTOffset } from '@/utils/calendar';
import { refetchQueries } from '@/utils/tanStackQuery';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants for entity prefixes — shared between Steno and Agent
export const ENTITY_PREFIXES = {
  NAVIGATE: 'navigate:',
  PROJECTS: 'projects:',
  CATEGORIES: 'categories:',
  ACTIONS: 'actions:',
  PEOPLE: 'people:',
  PEOPLE_GENERIC: 'people',
} as const;

/**
 * Shared hook for processing affected entities from both Steno and the RPM Agent.
 * Handles refetching queries, Cronofy sync, and navigation based on entity prefixes.
 */
export function useAffectedEntities() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: cronofySyncEventMany } = useCronofySyncEventMany();
  const { updateSelectedDate } = useCalendarMonthlyStore();

  const projectId = location.pathname.startsWith('/projects/') ? location.pathname.split('/projects/')[1] : null;

  const extractIdFromEntity = useCallback((entity: string, prefix: string): string => {
    return entity.replace(prefix, '');
  }, []);

  const findEntitiesByPrefix = useCallback((entities: string[], prefix: string): string[] => {
    return entities.filter((item) => item.startsWith(prefix));
  }, []);

  const extractIds = useCallback(
    (entities: string[], prefix: string): string[] => {
      return findEntitiesByPrefix(entities, prefix).map((entity) => extractIdFromEntity(entity, prefix));
    },
    [findEntitiesByPrefix, extractIdFromEntity],
  );

  const handleNavigation = useCallback(
    (navigationEntity: string) => {
      const fullPath = extractIdFromEntity(navigationEntity, ENTITY_PREFIXES.NAVIGATE);

      if (fullPath.includes(':')) {
        const [path, dateStr] = fullPath.split(':');

        let localDate;
        if (dateStr.length === 7) {
          localDate = formatStringToDate(`${dateStr}-01`);
        } else {
          localDate = formatStringToDate(dateStr);
        }

        updateSelectedDate(localDate);
        navigate(path);
      } else {
        navigate(fullPath);
      }
    },
    [extractIdFromEntity, updateSelectedDate, navigate],
  );

  const handleProjectEntities = useCallback(
    (entities: string[]) => {
      const projectEntity = entities.find((item) => item.startsWith(ENTITY_PREFIXES.PROJECTS));
      if (projectEntity) {
        const id = extractIdFromEntity(projectEntity, ENTITY_PREFIXES.PROJECTS);
        refetchQueries([keys.project.detail(id).queryKey]);
      }
    },
    [extractIdFromEntity],
  );

  const handleCategoryEntities = useCallback(
    (entities: string[]) => {
      const categoryEntity = entities.find((item) => item.startsWith(ENTITY_PREFIXES.CATEGORIES));
      if (categoryEntity) {
        const id = extractIdFromEntity(categoryEntity, ENTITY_PREFIXES.CATEGORIES);
        refetchQueries([keys.project.category(id).queryKey]);
      }
    },
    [extractIdFromEntity],
  );

  const handleActionEntities = useCallback(
    (entities: string[]) => {
      const actionIds = extractIds(entities, ENTITY_PREFIXES.ACTIONS);

      if (actionIds.length > 0) {
        if (projectId) {
          refetchQueries([keys.project.detail(projectId).queryKey]);
        }

        const eventsToSync = actionIds.map((actionId: string) => ({
          eventId: actionId,
          eventType: 'Action' as const,
          deleted: false,
        }));
        cronofySyncEventMany({ events: eventsToSync });
      }
    },
    [extractIds, projectId, cronofySyncEventMany],
  );

  const handlePeopleEntities = useCallback(
    (entities: string[]) => {
      const genericPeopleEntity = entities.find((item) => item === ENTITY_PREFIXES.PEOPLE_GENERIC);
      if (genericPeopleEntity) {
        refetchQueries([keys.people.all.queryKey]);
      }

      const specificPeopleEntity = entities.find(
        (item) => item.startsWith(ENTITY_PREFIXES.PEOPLE) && item !== ENTITY_PREFIXES.PEOPLE_GENERIC,
      );
      if (specificPeopleEntity) {
        const peopleId = extractIdFromEntity(specificPeopleEntity, ENTITY_PREFIXES.PEOPLE);
        refetchQueries([keys.people.detail(peopleId).queryKey]);
      }
    },
    [extractIdFromEntity],
  );

  const handleNavigationEntities = useCallback(
    (entities: string[]) => {
      const navigationEntity = entities.find((item) => item.startsWith(ENTITY_PREFIXES.NAVIGATE));
      if (navigationEntity) {
        handleNavigation(navigationEntity);
      }
    },
    [handleNavigation],
  );

  const processAffectedEntities = useCallback(
    (entities: string[]) => {
      handleProjectEntities(entities);
      handleCategoryEntities(entities);
      handleActionEntities(entities);
      handlePeopleEntities(entities);
      handleNavigationEntities(entities);
      refetchQueriesForArchive();
    },
    [handleProjectEntities, handleCategoryEntities, handleActionEntities, handlePeopleEntities, handleNavigationEntities],
  );

  return { processAffectedEntities };
}
